import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Form, Modal, Spinner, Table } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { getRompslompExpenses, getRompslompMappings, putRompslompExpense, sendRompslompBatch, sendRompslompExpense } from '../../../services/RompslompService';
import { SendConfirmModal } from './RompslompSending';
import { apiMessage, euro } from './rompslompUtils';
import { decodeEntities, useRompslompLabels } from '../../../utils/rompslompLabels';

const STATUSES = ['ready', 'needs_supplier', 'needs_ledger', 'needs_treatment', 'needs_receipt', 'duplicate', 'held', 'sent', 'error'];

function Select({ value, options, onPick, label, empty, disabled, muted }) {
    return (
        <Form.Select size="sm" className={muted ? 'text-muted' : undefined} aria-label={label} disabled={disabled} value={value ?? ''} onChange={(e) => onPick(e.target.value === '' ? null : (Number.isNaN(Number(e.target.value)) ? e.target.value : Number(e.target.value)))}>
            <option value="">{empty}</option>
            {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
        </Form.Select>
    );
}

export default function RompslompExpenses({ sending, reloadSending }) {
    const { t } = useTranslation();
    const labels = useRompslompLabels();
    const [data, setData] = useState(null);
    const [maps, setMaps] = useState(null);
    const [filter, setFilter] = useState('all');
    const [search, setSearch] = useState('');
    const [confirm, setConfirm] = useState(null);
    const [dup, setDup] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [e, m] = await Promise.all([getRompslompExpenses(), getRompslompMappings()]);
            setData(e.data); setMaps(m.data);
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    }, [t]);
    useEffect(() => { load(); }, [load]);

    const save = async (id, field, value) => {
        try { setData((await putRompslompExpense(id, { [field]: value })).data); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    };

    const send = async () => {
        setBusy(true);
        try {
            const res = await sendRompslompExpense(confirm.expense_id, confirm.payload_hash);
            toast[res.data.result.ok ? 'success' : 'warning'](t(`rompslomp.log.status.${res.data.result.status}`));
            setConfirm(null);
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); load(); reloadSending(); }
    };

    const batch = async () => {
        setBusy(true);
        try {
            const res = (await sendRompslompBatch('expenses')).data.result;
            toast[res.stopped_because ? 'warning' : 'success'](t('rompslomp.sending.batchDone', { sent: res.sent, remaining: res.remaining }));
        } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); } finally { setBusy(false); load(); reloadSending(); }
    };

    const rows = useMemo(() => (data ? data.rows.filter((r) => (filter === 'all' || r.status === filter)
        && (!search || `${r.supplier} ${r.items}`.toLowerCase().includes(search.toLowerCase()))) : []), [data, filter, search]);

    if (!data || !maps) return <Spinner animation="border" />;
    const costs = maps.options.cost_accounts.map((a) => ({ id: a.id, label: `${a.path || decodeEntities(a.name)} - ${labels.account(a)}` }));
    const contacts = maps.options.suppliers.map((c) => ({ id: c.id, label: decodeEntities(c.name) }));
    const contactName = (id) => (contacts.find((c) => c.id === id) || {}).label || `#${id}`;
    const inheritedContact = (r) => !!r.contact_id && !(r.override && r.override.contact_id);
    const treatments = maps.keys.treatments.map((k) => ({ id: k, label: t(`rompslomp.treatments.${k}`) }));
    const s = data.summary.by_status;
    const canSend = sending.can_send;

    return (
        <>
            <p className="text-muted">{t('rompslomp.expenses.hint')}</p>
            <div className="d-flex flex-wrap gap-2 mb-3" data-testid="expense-status-counts">
                {STATUSES.map((k) => <span key={k} className="me-3">{t(`rompslomp.expenses.status.${k}`)}: <strong>{s[k]}</strong></span>)}
            </div>
            <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
                <Form.Select size="sm" className="rompslomp-filter" aria-label={t('rompslomp.expenses.filter')} value={filter} onChange={(e) => setFilter(e.target.value)}>
                    <option value="all">{t('rompslomp.expenses.allStatuses')}</option>
                    {STATUSES.map((k) => <option key={k} value={k}>{t(`rompslomp.expenses.status.${k}`)}</option>)}
                </Form.Select>
                <Form.Control size="sm" className="rompslomp-filter" placeholder={t('rompslomp.expenses.search')} value={search} onChange={(e) => setSearch(e.target.value)} />
                <Button size="sm" variant="danger" disabled={!canSend || !sending.batch_unlocked || busy || s.ready === 0} onClick={batch} data-testid="batch-expenses"
                    title={sending.batch_unlocked ? '' : t('rompslomp.sending.batchLockedHint')}>
                    {t('rompslomp.sending.sendAllReady', { max: sending.batch_max })}
                </Button>
            </div>
            <Table className="rompslomp-table rompslomp-expenses" size="sm" hover data-testid="expense-table">
                <thead><tr><th className="c-narrow">#</th><th>{t('rompslomp.preview.date')}</th><th>{t('rompslomp.mappings.supplier')}</th><th>{t('rompslomp.preview.total')}</th><th>{t('rompslomp.preview.state')}</th>
                    <th>{t('rompslomp.mappings.costLedger')}</th><th>{t('rompslomp.expenses.treatment')}</th><th>{t('rompslomp.mappings.contact')}</th><th className="c-actions" /></tr></thead>
                <tbody>
                    {rows.map((r) => (
                        <tr key={r.expense_id}>
                            <td className="c-narrow" data-label="#">{r.expense_id}</td><td data-label={t('rompslomp.preview.date')}>{r.date}</td>
                            <td data-label={t('rompslomp.mappings.supplier')}>{r.supplier}<div className="small text-muted">{r.items}</div></td>
                            <td data-label={t('rompslomp.preview.total')}>{euro(r.incl_cents)}</td>
                            <td data-label={t('rompslomp.preview.state')}>
                                <strong>{t(`rompslomp.expenses.status.${r.status}`)}</strong>
                                {(r.reasons && r.reasons.length > 0 ? r.reasons : (r.reason ? [r.reason] : [])).map((x) => <div key={x} className="small text-muted">{x}</div>)}
                                {r.invoice_number && <div className="small text-muted">{t('rompslomp.expenses.invoiceNumber')}: {r.invoice_number}</div>}
                                {r.auto && (
                                    <div className={`small ${r.auto.state === 'error' ? 'text-danger' : 'text-muted'}`} data-testid={`auto-state-${r.expense_id}`}>
                                        {r.auto.state === 'waiting' && r.auto.due_at ? t('rompslomp.expenses.auto.waitingUntil', { time: r.auto.due_at.replace('T', ' ') })
                                            : t(`rompslomp.expenses.auto.${r.auto.state}`)}
                                        {r.auto.state === 'error' && r.auto.reason ? `: ${r.auto.reason}` : ''}
                                    </div>
                                )}
                                {r.flags.includes('changed_after_sending') && <div className="small text-danger">{t('rompslomp.expenses.changedAfterSending')}</div>}

                            </td>
                            <td data-label={t('rompslomp.mappings.costLedger')}><Select label={`${t('rompslomp.mappings.costLedger')} ${r.expense_id}`} value={r.ledger_account_id} options={costs} empty={t('rompslomp.mappings.notMapped')} onPick={(v) => save(r.expense_id, 'ledger_account_id', v)} disabled={r.status === 'sent'} /></td>
                            <td data-label={t('rompslomp.expenses.treatment')}><Select label={`${t('rompslomp.expenses.treatment')} ${r.expense_id}`} value={r.vat_treatment} options={treatments} empty={t('rompslomp.mappings.notMapped')} onPick={(v) => save(r.expense_id, 'vat_treatment', v)} /></td>
                            <td data-label={t('rompslomp.mappings.contact')}><Select label={`${t('rompslomp.mappings.contact')} ${r.expense_id}`} value={inheritedContact(r) ? null : r.contact_id} options={contacts} muted={!!inheritedContact(r)}
                                empty={inheritedContact(r) ? `${contactName(r.contact_id)} ${t('rompslomp.expenses.fromSupplier')}` : t('rompslomp.mappings.notMapped')} onPick={(v) => save(r.expense_id, 'contact_id', v)} /></td>
                            <td className="c-actions">
                                {r.status === 'duplicate' && <Button size="sm" variant="outline-warning" onClick={() => setDup(r)}>{t('rompslomp.expenses.override')}</Button>}
                                <Button size="sm" variant="outline-danger" disabled={r.status !== 'ready' || !canSend || !r.payload} onClick={() => setConfirm(r)} data-testid={`send-expense-${r.expense_id}`}>
                                    {t('rompslomp.sending.sendThis')}
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>
            <SendConfirmModal show={!!confirm} title={confirm ? `${t('rompslomp.sending.sendThis')}: #${confirm.expense_id} ${confirm.supplier}` : ''}
                payload={confirm?.payload} hash={confirm?.payload_hash} busy={busy} onConfirm={send} onHide={() => setConfirm(null)} />
            <Modal show={!!dup} onHide={() => setDup(null)} centered>
                <Formik initialValues={{ reason: '' }} validationSchema={Yup.object({ reason: Yup.string().trim().min(5, t('rompslomp.expenses.reasonShort')).required(t('rompslomp.expenses.reasonShort')) })}
                    onSubmit={async (v, { setSubmitting }) => { await save(dup.expense_id, 'duplicate_override_reason', v.reason.trim()); setSubmitting(false); setDup(null); }}>
                    {({ handleSubmit, handleChange, values, errors, touched }) => (
                        <Form noValidate onSubmit={handleSubmit}>
                            <Modal.Header closeButton><Modal.Title>{t('rompslomp.expenses.override')}</Modal.Title></Modal.Header>
                            <Modal.Body>
                                <p>{dup?.reason}</p>
                                <Form.Group controlId="dup-reason"><Form.Label>{t('rompslomp.expenses.reason')}</Form.Label>
                                    <Form.Control name="reason" value={values.reason} onChange={handleChange} isInvalid={touched.reason && !!errors.reason} />
                                    <Form.Control.Feedback type="invalid">{errors.reason}</Form.Control.Feedback></Form.Group>
                            </Modal.Body>
                            <Modal.Footer><Button type="submit">{t('rompslomp.expenses.override')}</Button></Modal.Footer>
                        </Form>
                    )}
                </Formik>
            </Modal>
        </>
    );
}
